import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import Link from "next/link";
import { media } from "@/styles/media";
import { theme } from "@/styles/theme";
import Wave from "./WaveText";

// Banner - Type
interface OwnProps {
  $bgUrl?: string;
  $descriptionColor?: string;
}
interface IBannerType {
  id: number;
  title: string;
  description: string;
  descriptionColor: string;
  buttonText: string;
  buttonColor: string;
  bgUrl: string;
  isVideo: boolean;
  link: string;
}

export default function Hero() {
  const { t } = useTranslation();
  // Banner Data
  const bannerDatas: IBannerType[] = [
    {
      id: 1,
      title: t("main.hero.banner-1.title"),
      description: t("main.hero.banner-1.desc"),
      descriptionColor: "black",
      buttonColor: "#971b2f",
      buttonText: t("main.hero.button-1"),
      bgUrl: "/main/Slider_Banner_02-scaled.jpg",
      isVideo: false,
      link: "/product/inbody580",
    },
    {
      id: 2,
      title: t("main.hero.banner-2.title"),
      description: t("main.hero.banner-2.desc"),
      descriptionColor: "black",
      buttonColor: "black",
      buttonText: t("main.hero.button-1"),
      bgUrl: "/main/InGrip_Banner-2.webp",
      isVideo: false,
      link: "/product/ingrip",
    },
    {
      id: 3,
      title: t("main.hero.banner-3.title"),
      description: t("main.hero.banner-3.desc"),
      descriptionColor: "white",
      buttonColor: "#971b2f",
      buttonText: t("main.hero.button-1"),
      bgUrl: "https://inbodyasia.com/wp-content/uploads/2024/11/inbody970_video_landing_banner.mp4",
      isVideo: true,
      link: "/product/inbody970",
    },
    {
      id: 4,
      title: t("main.hero.banner-4.title"),
      description: t("main.hero.banner-4.desc"),
      descriptionColor: "black",
      buttonColor: "#971b2f",
      buttonText: t("main.hero.button-2"),
      bgUrl: "https://inbodyasia.com/wp-content/uploads/2024/11/lookingbody_banner_video.mp4",
      isVideo: true,
      link: "/product/lookinbody-web",
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const resetTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    const newIntervalId = setInterval(goToNextSlide, 8000);
    intervalRef.current = newIntervalId;
  };

  const goToNextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % bannerDatas.length);
  };
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    resetTimer();
  };

  useEffect(() => {
    resetTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <HeroWrapper>
      <CarouselWrapper
        style={{
          transform: `translateX(-${currentIndex * 100}%)`,
        }}
      >
        {bannerDatas.map((data) => (
          <Banner key={data.id}>
            {!data.isVideo ? (
              <BannerImage $bgUrl={data.bgUrl} />
            ) : (
              <>
                <VideoCover />
                <BannerVideo>
                  <BannerVideoElement src={data.bgUrl} preload="metadata" muted autoPlay playsInline loop />
                </BannerVideo>
              </>
            )}
            <Content>
              <Wave key={`${data.id}-${currentIndex}`} text={data.title} />
              <Title key={`title-${data.id}-${currentIndex}`}>{data.title}</Title>
              <ContentText key={`text-${data.id}-${currentIndex}`} $descriptionColor={data.descriptionColor}>
                {data.description}
              </ContentText>
              <br />
              <LinkButton key={`btn-${data.id}-${currentIndex}`} $btnColor={data.buttonColor} href={data.link}>
                {data.buttonText}
              </LinkButton>
            </Content>
          </Banner>
        ))}
      </CarouselWrapper>
      <CarouselPagination>
        {bannerDatas.map((data, index) => (
          <CarouselPage
            key={data.id}
            onClick={() => goToSlide(index)}
            style={{
              backgroundColor: currentIndex === index ? `${theme.colors.primary}` : "rgba(151, 27, 48, 0.2)",
            }}
          />
        ))}
      </CarouselPagination>
    </HeroWrapper>
  );
}

// Banner - Styles
const HeroWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 875px;
  overflow: hidden;
`;

const Banner = styled.div`
  width: 100%;
  position: relative;
  flex: 0 0 100%;
`;

const BannerImage = styled.div<OwnProps>`
  width: 100%;
  height: 875px;
  display: block;
  object-fit: cover;
  background-image: url(${(props) => props.$bgUrl});
  background-size: cover;
  background-position: center 100%;
`;

const BannerVideo = styled.div`
  width: 100%;
  height: 875px;
  display: block;
`;

const BannerVideoElement = styled.video`
  width: 100%;
  height: 100%;
  opacity: 0.9;
  object-fit: cover;
`;

const VideoCover = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: url(./main/grid.png);
`;

const Content = styled.div`
  position: absolute;
  top: 30%;
  left: 10%;
  padding: 0 100px;
  @media (max-width: 1116px) {
    left: 0;
  }
`;

const fadeAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(100%);
  }
  100%{
    opacity: 1;
    transform: translateY(0);
  }
`;

const Title = styled.p`
  width: 100%;
  margin: 0 10px 0 0;
  display: none;
  font-size: 2.8rem;
  font-weight: 700;
  white-space: pre-wrap;
  line-height: 120%;
  text-shadow: 2px 2px 2px rgba(1, 1, 1, 0.1);
  opacity: 0;
  color: ${theme.colors.primary};
  animation: ${fadeAnimation} 0.6s ease-out 1000ms forwards;
  ${media.tablet`
    display: block;  
  `}
`;

const ContentText = styled.span<OwnProps>`
  display: inline-block;
  white-space: pre-wrap;
  font-size: 1.5rem;
  line-height: 150%;
  text-shadow: ${(props) =>
    props.$descriptionColor === "black" ? "2px 2px 8px rgba(255,255,255,0.8)" : "2px 2px 8px rgba(0,0,0,0.8)"};
  opacity: 0;
  color: ${(props) => props.$descriptionColor};
  animation: ${fadeAnimation} 0.6s ease-out 1000ms forwards;
  ${media.desktop`
    font-size: 0.9rem;
  `}
  ${media.tablet`
    font-size: 0.8rem;
  `}
`;

const LinkButton = styled(Link)<{ $btnColor: string }>`
  display: inline-block;
  margin-top: 50px;
  padding: 10px 30px;
  float: left;
  font-size: 0.8rem;
  opacity: 0;
  border-radius: 50px;
  text-align: center;
  cursor: pointer;
  animation: ${fadeAnimation} 0.6s ease-out 1300ms forwards;
  background-color: ${({ $btnColor }) => $btnColor};
  color: ${theme.colors.text.white};
`;

const CarouselWrapper = styled.div`
  display: flex;
  transition: transform 0.5s;
`;

const CarouselPagination = styled.div`
  position: absolute;
  bottom: 30px;
  left: 50%;
  display: flex;
  transform: translateX(-50%);
`;

const CarouselPage = styled.button`
  width: 55px;
  height: 6px;
  border-radius: 20px;
  margin: 7px;
  background-color: rgba(151, 27, 48, 0.3);
  border: none;
  cursor: pointer;
`;
